import React from "react";
import { Box, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { ImageMetadata } from "../types";
import { getFilterDisplay } from "../utils/getFilterDisplay";
import { useUI } from "../contexts/UIContext";

interface FrameDetailsTableProps {
  imagesForView: ImageMetadata[];
  frameTypeFilter: string;
  setFrameTypeFilter: (filter: string) => void;
  filterTypeFilter: string;
  setFilterTypeFilter: (filter: string) => void;
}

const FrameDetailsTable: React.FC<FrameDetailsTableProps> = ({
  imagesForView,
  frameTypeFilter,
  setFrameTypeFilter,
  filterTypeFilter,
  setFilterTypeFilter,
}) => {
  const { filterSettings } = useUI();
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        p: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        minHeight: 340,
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl
          size="small"
          sx={{
            minWidth: 160,
            bgcolor: "#181818",
            borderRadius: 1,
          }}
        >
          <InputLabel sx={{ color: "text.secondary" }}>Frame Type</InputLabel>
          <Select
            value={frameTypeFilter}
            label="Frame Type"
            onChange={(e) => setFrameTypeFilter(e.target.value)}
            sx={{
              color: "text.primary",
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#333",
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="LIGHT">LIGHT</MenuItem>
            <MenuItem value="DARK">DARK</MenuItem>
            <MenuItem value="FLAT">FLAT</MenuItem>
            <MenuItem value="BIAS">BIAS</MenuItem>
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{
            minWidth: 160,
            bgcolor: "#181818",
            borderRadius: 1,
          }}
        >
          <InputLabel sx={{ color: "text.secondary" }}>Filter</InputLabel>
          <Select
            value={filterTypeFilter}
            label="Filter"
            onChange={(e) => setFilterTypeFilter(e.target.value)}
            sx={{
              color: "text.primary",
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#333",
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {Array.from(
              new Set(
                imagesForView.map((img) => img.FilterName).filter(Boolean)
              )
            ).map((f) => {
              const display = getFilterDisplay(f, filterSettings);
              if (!display) return null;
              const { name } = display;
              return (
                <MenuItem key={f} value={f}>
                  {name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          overflow: "auto",
          maxHeight: 420,
          borderRadius: 2,
          border: "1px solid #222",
          bgcolor: "#181818",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            color: "#fff",
            tableLayout: "fixed",
            fontFamily: "Segoe UI",
          }}
        >
          <colgroup>
            <col style={{ width: "48px" }} /> {/* Seq */}
            <col style={{ width: "72px" }} /> {/* Type */}
            <col style={{ width: "72px" }} /> {/* Filter */}
            <col style={{ width: "80px" }} /> {/* Time */}
            <col style={{ width: "90px" }} /> {/* Duration */}
            <col style={{ width: "70px" }} /> {/* HFR */}
            <col style={{ width: "80px" }} /> {/* Guiding */}
            <col style={{ width: "70px" }} /> {/* Stars */}
            <col style={{ width: "70px" }} /> {/* Temp */}
          </colgroup>
          <thead>
            <tr style={{ background: "#222" }}>
              <th
                style={{
                  padding: "10px 8px",
                  paddingLeft: "30px",
                  fontWeight: 700,
                  textAlign: "left",
                }}
              >
                Seq
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "left",
                }}
              >
                Type
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "left",
                }}
              >
                Filter
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "left",
                }}
              >
                Time
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "right",
                }}
              >
                Duration
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "right",
                }}
              >
                HFR
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "right",
                }}
              >
                Guiding
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  fontWeight: 700,
                  textAlign: "right",
                }}
              >
                Stars
              </th>
              <th
                style={{
                  padding: "10px 8px",
                  paddingRight: "30px",
                  fontWeight: 700,
                  textAlign: "right",
                }}
              >
                Temp
              </th>
            </tr>
          </thead>
          <tbody>
            {imagesForView
              .filter(
                (img) =>
                  frameTypeFilter === "All" ||
                  ("FrameType" in img &&
                    typeof (img as any).FrameType === "string" &&
                    (img as any).FrameType === frameTypeFilter) ||
                  (frameTypeFilter === "LIGHT" &&
                    !("FrameType" in img) &&
                    img.FilterName)
              )
              .filter(
                (img) =>
                  filterTypeFilter === "All" ||
                  img.FilterName === filterTypeFilter
              )
              .map((img, i) => {
                // Type guard for FrameType
                const frameType =
                  "FrameType" in img &&
                  typeof (img as any).FrameType === "string"
                    ? (img as any).FrameType
                    : img.FilterName
                    ? "LIGHT"
                    : "";
                // Time cell logic
                let date = "";
                if (img.FilePath) {
                  const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
                  if (match) date = match[1];
                }
                let time = "";
                if (img.ExposureStart && img.ExposureStart.includes(" ")) {
                  time = img.ExposureStart.split(" ")[1].slice(0, 5); // HH:mm
                }
                let timeCell = "—";
                if (date && time) timeCell = `${date} ${time}`;
                else if (date) timeCell = date;
                else if (time) timeCell = time;
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #222" }}>
                    <td
                      style={{
                        padding: "10px 8px",
                        paddingLeft: "30px",
                        textAlign: "left",
                      }}
                    >
                      {i + 1}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "left" }}>
                      {frameType}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "left" }}>
                      {img.FilterName || "—"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "left" }}>
                      {timeCell}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "right" }}>
                      {img.Duration ? img.Duration.toFixed(2) + "s" : "—"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "right" }}>
                      {typeof img.HFR === "number" ? img.HFR.toFixed(2) : "—"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "right" }}>
                      {typeof img.GuidingRMSArcSec === "number"
                        ? img.GuidingRMSArcSec.toFixed(2)
                        : "—"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "right" }}>
                      {typeof img.DetectedStars === "number"
                        ? img.DetectedStars
                        : "—"}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        paddingRight: "30px",
                        textAlign: "right",
                      }}
                    >
                      {typeof img.CameraTemp === "number"
                        ? img.CameraTemp.toFixed(1)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default FrameDetailsTable;
